import type {
  AnnotationNotationAttributes,
  CircleNotationAttributes,
  CurveNotationAttributes,
  LineNotationAttributes,
  NotationAttributes,
  PointNotationAttributes,
  RectNotationAttributes,
} from "common/baseTypes";
import type { LessonTemplateNotation } from "common/lessonTemplates";
import {
  practiceDiagramStems,
  type PracticeQuestionTemplate,
} from "common/practiceQuestionTemplates";
import type { UserAttributes } from "common/userTypes";

/**
 * Owner of catalog stem figures drawn from the bundled template. Deliberately
 * not the signed-in or guest user, so the figure renders like any other
 * read-only QUESTION layer (no "my notation" styling).
 */
export const PRACTICE_STEM_OWNER: UserAttributes = {
  uuid: "00000000-0000-4000-8000-00000000c0de",
  firstName: "Catalog",
  lastName: "",
  email: "",
  password: "",
  access_token: null,
  reset_pasword_token: null,
  imageUrl: "",
  userType: "TEACHER",
  approved: true,
};

/** Stable per question + position, so reloads and tests see the same ids. */
export function practiceStemNotationUUId(
  questionUUId: string,
  index: number,
): string {
  return `${questionUUId}-stem-${index}`;
}

function toStemNotation(
  item: LessonTemplateNotation,
  questionUUId: string,
  index: number,
): NotationAttributes | null {
  const base = {
    uuid: practiceStemNotationUUId(questionUUId, index),
    parentUUId: questionUUId,
    boardType: "QUESTION" as const,
    user: PRACTICE_STEM_OWNER,
  };

  switch (item.kind) {
    case "LINE": {
      const n: LineNotationAttributes = {
        ...base,
        notationType: "LINE",
        p1x: item.p1x,
        p1y: item.p1y,
        p2x: item.p2x,
        p2y: item.p2y,
        dashed: item.dashed ?? false,
        arrowLeft: item.arrowLeft ?? false,
        arrowRight: item.arrowRight ?? false,
        color: null,
      };
      return n;
    }
    case "CURVE": {
      const n: CurveNotationAttributes = {
        ...base,
        notationType: "CURVE",
        p1x: item.p1x,
        p1y: item.p1y,
        p2x: item.p2x,
        p2y: item.p2y,
        cpx: item.cpx,
        cpy: item.cpy,
        color: null,
      };
      return n;
    }
    case "CIRCLE": {
      const n: CircleNotationAttributes = {
        ...base,
        notationType: "CIRCLE",
        cx: item.cx,
        cy: item.cy,
        r: item.r,
      };
      return n;
    }
    case "ANNOTATION": {
      const n: AnnotationNotationAttributes = {
        ...base,
        notationType: "ANNOTATION",
        x: item.x,
        y: item.y,
        value: item.value,
        rotation: 0,
      };
      return n;
    }
    case "TEXT": {
      const n: RectNotationAttributes = {
        ...base,
        notationType: "TEXT",
        fromCol: item.fromCol,
        toCol: item.toCol,
        fromRow: item.fromRow,
        toRow: item.toRow,
        value: item.value,
      };
      return n;
    }
    case "SYMBOL":
    case "EXPONENT": {
      const n: PointNotationAttributes = {
        ...base,
        notationType: item.kind,
        col: item.col,
        row: item.row,
        value: item.value,
      };
      return n;
    }
    default:
      return null;
  }
}

/** Symbols on a row that has an exponent, so 2⁵ is drawn instead of left off the grid. */
function expressionNotations(
  items: readonly LessonTemplateNotation[],
): LessonTemplateNotation[] {
  const rows = new Set<number>();
  for (const item of items) {
    if (item.kind === "EXPONENT") rows.add(item.row);
  }
  if (!rows.size) return [];
  return items.filter(
    (item) =>
      (item.kind === "SYMBOL" || item.kind === "EXPONENT") &&
      rows.has(item.row),
  );
}

function questionExpressionNotations(
  notations: readonly NotationAttributes[],
): NotationAttributes[] {
  const rows = new Set<number>();
  for (const notation of notations) {
    if (notation.notationType !== "EXPONENT") continue;
    const row = (notation as PointNotationAttributes).row;
    if (typeof row === "number") rows.add(row);
  }
  if (!rows.size) return [];
  return notations.filter((notation) => {
    if (notation.notationType !== "SYMBOL" && notation.notationType !== "EXPONENT") {
      return false;
    }
    const row = (notation as PointNotationAttributes).row;
    return typeof row === "number" && rows.has(row);
  });
}

/**
 * Read-only figure for a catalog question, built from the bundled template.
 *
 * The template is the source of truth (the seeder copies it into the DB), so
 * drawing from it gives the same board offline, in test, or before seeding.
 * Only diagram stems are returned — lines, curves, circles, labels, and vertex
 * letters. The problem text goes to the problem pane, not the board.
 */
/**
 * What a practice board actually paints. Student work stays; the question's
 * text stays in the problem pane. The drawn figure (shapes plus their labels)
 * is the part of the question layer that belongs on the grid.
 */
export function notationsForPracticePaint(
  notations: NotationAttributes[],
): NotationAttributes[] {
  const student = notations.filter((n) => n.boardType !== "QUESTION");
  const question = notations.filter((n) => n.boardType === "QUESTION");
  const figure = practiceDiagramStems(question);
  const expression = questionExpressionNotations(question);
  const seen = new Set<string>();
  const generated = question.filter((notation) => notation.uuid.includes("-gen-"));
  const paintedQuestion = [...figure, ...expression, ...generated].filter((notation) => {
    if (seen.has(notation.uuid)) return false;
    seen.add(notation.uuid);
    return true;
  });
  return [...paintedQuestion, ...student];
}

/** Figure minted with a practice instance. Every mark is drawn, including a data list. */
export function practiceFigureStepsOverlay(
  items: readonly LessonTemplateNotation[],
  stemKey: string,
): NotationAttributes[] {
  const overlay: NotationAttributes[] = [];
  items.forEach((item, index) => {
    const notation = toStemNotation(item, stemKey, index);
    if (notation) overlay.push(notation);
  });
  return overlay;
}

export function practiceGeneratedFigureOverlay(
  items: readonly LessonTemplateNotation[],
  questionUUId: string,
): NotationAttributes[] {
  return practiceFigureStepsOverlay(items, `${questionUUId}-gen`);
}

export function practiceTemplateDiagramOverlay(
  template: PracticeQuestionTemplate,
  questionUUId: string,
): NotationAttributes[] {
  const stems = new Set<LessonTemplateNotation>([
    ...practiceDiagramStems(template.notations),
    ...expressionNotations(template.notations),
  ]);
  const overlay: NotationAttributes[] = [];
  template.notations.forEach((item, index) => {
    if (!stems.has(item)) return;
    const notation = toStemNotation(item, questionUUId, index);
    if (notation) overlay.push(notation);
  });
  return overlay;
}
